import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  ChevronRight, 
  Sparkles, 
  Shield, 
  TrendingUp,
  Zap,
  Lock,
  Code,
  ArrowRight
} from 'lucide-react';

const LearnPage = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const topics = [
    {
      id: 1,
      icon: Sparkles,
      title: 'DeFi Basics',
      description: 'Learn the fundamental concepts and principles of decentralized finance',
      color: 'from-purple-600 to-orange-500',
      content: [
        'What is DeFi? Definition and characteristics of decentralized finance',
        'Differences and advantages between DeFi and traditional finance',
        'Main types of DeFi protocols: DEX, lending, liquidity mining, etc.',
        'How to safely participate in the DeFi ecosystem',
        'Wallet setup and fund management best practices'
      ]
    },
    {
      id: 2,
      icon: Code,
      title: 'Smart Contract Basics',
      description: 'Learn Solidity and smart contract development',
      color: 'from-blue-500 to-cyan-600',
      content: [
        'Solidity programming language fundamentals',
        'Smart contract structure and lifecycle',
        'Common security patterns and best practices',
        'Testing and deploying smart contracts',
        'Methods for interacting with DeFi protocols'
      ]
    },
    {
      id: 3,
      icon: TrendingUp,
      title: 'Trading Strategy Design',
      description: 'Learn how to design and optimize DeFi trading strategies',
      color: 'from-green-500 to-emerald-600',
      content: [
        'Liquidity provision strategies (LP)',
        'Arbitrage and hedging strategies',
        'Yield farming and liquidity mining',
        'Risk management techniques',
        'Strategy backtesting and optimization methods'
      ]
    },
    {
      id: 4,
      icon: Shield,
      title: 'Security Best Practices',
      description: 'Protect your assets and strategy security',
      color: 'from-red-500 to-rose-600',
      content: [
        'Common security vulnerabilities and attack methods',
        'How to audit smart contracts',
        'Multi-signature and access control',
        'Insurance and risk management tools',
        'Emergency response plans'
      ]
    },
    {
      id: 5,
      icon: Zap,
      title: 'AI-Assisted Strategy Development',
      description: 'Use AI tools to improve strategy development efficiency',
      color: 'from-yellow-500 to-orange-600',
      content: [
        'How to use AI to generate strategy code',
        'Using AI security analysis tools',
        'Automated strategy optimization',
        'Market analysis and prediction',
        'AI-assisted decision systems'
      ]
    },
    {
      id: 6,
      icon: Lock,
      title: 'Advanced Topics',
      description: 'Explore advanced applications of DeFi in depth',
      color: 'from-pink-500 to-purple-600',
      content: [
        'Cross-chain DeFi protocols',
        'Derivatives and structured products',
        'DAO governance and voting',
        'Layer 2 solutions',
        'Future development trends of DeFi'
      ]
    }
  ];

  const resources = [
    {
      title: 'Official Documentation',
      description: 'View complete API documentation and usage guides',
      link: '#'
    },
    {
      title: 'Video Tutorials',
      description: 'Watch detailed video tutorials and demonstrations',
      link: '#'
    },
    {
      title: 'Community Forum',
      description: 'Join community discussions and get help',
      link: '#'
    },
    {
      title: 'GitHub Examples',
      description: 'View open source code examples and templates',
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen pt-16 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 mb-6">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">DeFi Learning Center</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Learn DeFi from scratch, let AI be your learning assistant
          </p>
        </motion.div>

        {/* Learning Topics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {topics.map((topic, index) => {
            const Icon = topic.icon;
            const isExpanded = expandedCard === topic.id;
            
            return (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 cursor-pointer"
                onClick={() => setExpandedCard(isExpanded ? null : topic.id)}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${topic.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{topic.title}</h3>
                <p className="text-gray-400 mb-4">{topic.description}</p>
                
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-purple-500/20"
                  >
                    <ul className="space-y-2">
                      {topic.content.map((item, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-300">
                          <ChevronRight className="w-4 h-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
                
                <div className="flex items-center text-purple-400 text-sm mt-4">
                  <span>{isExpanded ? 'Collapse' : 'Expand Details'}</span>
                  <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Start Guide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card p-8 mb-12"
        >
          <h2 className="text-3xl font-bold mb-6 text-white">🚀 Quick Start Guide</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <span className="text-purple-400 font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Learn Basics</h3>
              <p className="text-gray-400">
                Learn the fundamental concepts of DeFi and understand how decentralized finance works and its advantages
              </p>
            </div>
            <div className="flex flex-col">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <span className="text-purple-400 font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Generate Strategy</h3>
              <p className="text-gray-400">
                Use the AI strategy generator to create your first DeFi trading strategy
              </p>
            </div>
            <div className="flex flex-col">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <span className="text-purple-400 font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Security Analysis</h3>
              <p className="text-gray-400">
                Use AI security analysis tools to check your strategy and ensure security
              </p>
            </div>
          </div>
        </motion.div>

        {/* Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="card p-8"
        >
          <h2 className="text-3xl font-bold mb-6 text-white">📚 Learning Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resources.map((resource, index) => (
              <motion.a
                key={index}
                href={resource.link}
                whileHover={{ scale: 1.02 }}
                className="p-6 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{resource.title}</h3>
                    <p className="text-gray-400 text-sm">{resource.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-12 text-center"
        >
          <div className="card p-8 inline-block">
            <h2 className="text-2xl font-bold mb-4 text-white">Ready to Get Started?</h2>
            <p className="text-gray-400 mb-6">Start using AI tools to generate your first DeFi strategy now</p>
            <motion.a
              href="/generate"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <span>Generate Strategy</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LearnPage;


